import { Injectable, NgZone } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmInputComponent } from './components/confirm-input/confirm-input.component';
import { NumberInputComponent } from './components/number-input/number-input.component';
import { StringInputComponent } from './components/string-input/string-input.component';
import { DoubleInputComponent } from './components/double-input/double-input.component';
import { GameDialogService } from 'src/app/game/page/game/gameDialog.service';
import { firstValueFrom, Subject, Subscription } from 'rxjs';
import { GameStateId } from 'src/app/core/state/game/game.state';
import { MultiDaySettingsInputComponent } from './components/multi-day-settings-input/multi-day-settings-input.component';
import { IBlindLevelAndResumeDate } from '@soposopo/sopo-models/dist/tournament/Tournament.state';
import moment from 'moment';
import { PhoneNumberInputComponent } from './components/phone-number-input/phone-number-input.component';

export const DEFAULT_DIALOG_OPTIONS: DialogOptions = {
    title: '',
    body: '',
    cancelText: 'Cancel',
    okText: 'Ok',
    placeholder: '',
    placeholderFirst: '',
    placeholderSecond: '',
    numbersOnly: false,
    cancelReturn: false,
    maxlength: 0,
    clubBalance: 0,
    senderID: '',
    receiverID: '',
    isAdmin: false,
    cancelAfter: 0,
    dontShowAgain: '',
    inputType: 'text',
    cancelButtonClass: '',
    bolderBody: '',
    clubName: '',
    inGame: false,
};

export const DEFAULT_MULTI_DAY_SETTINGS_INPUT_OPTIONS: IMultiDaySettingsInputOptions = {
    blinds: [],
    disabledToIndex: -1,
    minTimeString: moment(new Date).toISOString(true), 
    cancelText: 'cancel',
    okText: 'Add',
}

@Injectable({
    providedIn: 'root',
})
export class DialogInputService {
    constructor(
        private matDialog: MatDialog, 
        private gameDialog: GameDialogService,
        private zone: NgZone,
    ) {}

    async promptForConfirmation(options: DialogOptions, backdropClass?: string, gameStateId?: GameStateId, disableClose?: boolean, closeSubject?: Subject<void>): Promise<boolean> {
        return this.zone.run(async () => {
            const customOptions: DialogOptions = { ...DEFAULT_DIALOG_OPTIONS, ...options };
            const prompt = this.matDialog.open(ConfirmInputComponent, {
                data: customOptions,
                panelClass: options.panelClass,
                backdropClass,
                disableClose
            });
            let timeout;
            if(options.autoClose) {
                timeout = setTimeout(() => {
                    prompt.close()
                }, 1000 * options.autoClose);
            }
            if (gameStateId != undefined) {
                this.gameDialog.changeMatDialogClassOnViewChange(gameStateId, prompt);
            }
            let closeSubscription: Subscription;
            if (closeSubject) {
                closeSubscription = closeSubject.subscribe(() => prompt.close())
            }
            return firstValueFrom(prompt.afterClosed())
                .then((data) => {
                    if (closeSubscription) closeSubscription.unsubscribe();
                    clearTimeout(timeout);
                    return data != null && data.confirmed === true;
                });
        })
    }

    async promptForNumber(options: DialogOptions): Promise<any> {
        const customOptions: DialogOptions = { ...DEFAULT_DIALOG_OPTIONS, ...options };
        const prompt = this.matDialog.open(NumberInputComponent, {
            data: customOptions,
            panelClass: options.panelClass,
        });
        return firstValueFrom(prompt.afterClosed())
            .then((data) => {
                if (data == null) {
                    return undefined;
                } else {
                    return data;
                }
            });
    }

    async promptForNumberAsString(options: DialogOptions): Promise<String> {
        const customOptions: DialogOptions = { ...DEFAULT_DIALOG_OPTIONS, ...options, numbersOnly: true };
        const prompt = this.matDialog.open(StringInputComponent, {
            data: customOptions,
            panelClass: options.panelClass,
        });
        return firstValueFrom(prompt.afterClosed())
            .then((data) => {
                if (data == null) {
                    return undefined;
                } else {
                    return data.value;
                }
            });
    }

    async promptForString(options: DialogOptions, disableClose = false): Promise<string> {
        const customOptions: DialogOptions = { ...DEFAULT_DIALOG_OPTIONS, ...options };
        const prompt = this.matDialog.open(StringInputComponent, {
            data: customOptions,
            panelClass: options.panelClass,
            disableClose
        });
        return firstValueFrom(prompt.afterClosed())
            .then((data) => {
                if (data == null) {
                    return undefined;
                } else {
                    return data.value;
                }
            });
    }

    async promptForDoubleInput(options: DialogOptions): Promise<any> {
        const customOptions: DialogOptions = { ...DEFAULT_DIALOG_OPTIONS, ...options };
        const prompt = this.matDialog.open(DoubleInputComponent, {
            data: customOptions,
            panelClass: options.panelClass,
        });
        return firstValueFrom(prompt.afterClosed())
            .then((data) => {
                if (data == null) {
                    return undefined;
                } else {
                    return data;
                }
            });
    }

    promtFotMultiDaySettings(options: IMultiDaySettingsInputOptions): Promise<IBlindLevelAndResumeDate | undefined> {
        const customOptions = {...DEFAULT_MULTI_DAY_SETTINGS_INPUT_OPTIONS, ...options}
        const prompt = this.matDialog.open(MultiDaySettingsInputComponent, {
            data: customOptions,
        });
        return firstValueFrom(prompt.afterClosed());
    }

    async closePrompt() {
        if (this.matDialog.openDialogs.length > 0) {
            this.matDialog.closeAll();
        }
    }
}

export interface IMultiDaySettingsInputOptions {
    blinds: string[];
    disabledToIndex: number;
    minTimeString: string;
    cancelText?: string;
    okText?: string;
}

export interface DialogOptions {
    title?: string;
    body?: string;
    panelClass?: string;
    cancelText?: string;
    okText?: string;
    placeholder?: string;
    placeholderFirst?: string;
    placeholderSecond?: string;
    numbersOnly?: boolean;
    cancelReturn?: boolean;
    maxlength?: number;
    clubBalance?: number;
    senderID?: any;
    receiverID?: any;
    receiverIDs?: any;
    isAdmin?: boolean;
    senderIDs?: any[];
    selectedMembersBalance?: any[];
    closeOnCommand?: Subject<null>;
    cancelAfter?: number;
    dontShowAgain?: string;
    warning?: string;
    autoClose?: number;
    inputType?: 'text' | 'password';
    cancelButtonClass?: string;
    deepLinkClubId?: string;
    bolderBody?: string;
    clubName?: string;
    inGame?: boolean;
}
